import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar, AlertCircle, ArrowLeft, ChevronRight } from "lucide-react";
import { CampaignType } from "@/constants/campaign-types";
import { CampaignTypeDropdownSelector } from "../campaign-type-dropdown-selector";
import { isEndDateValid } from "../../../utils/date-utils";
import { PRO014_DESCRIPTION_MAX_LENGTH } from "../../constants";

export interface Pro014StepOneData {
  description: string;
  startDate: string;
  endDate: string;
}

interface StepOneProps {
  data: Pro014StepOneData;
  updateData: (data: Partial<Pro014StepOneData>) => void;
  nextStep: () => void;
  onChangeType?: (type: CampaignType) => void;
}

export default function StepOne({ data, updateData, nextStep, onChangeType }: StepOneProps) {
  const navigate = useNavigate();
  const [campaignType, setCampaignType] = useState<CampaignType>(CampaignType.PRO014);
  const [errors, setErrors] = useState({
    campaignType: "",
    description: "",
    startDate: "",
    endDate: "",
  });

  const validateForm = () => {
    const newErrors = {
      campaignType: "",
      description: "",
      startDate: "",
      endDate: "",
    };

    // Validação tipo de campanha
    if (!campaignType) {
      newErrors.campaignType = "Selecione um tipo de campanha";
    }

    // Validação da descrição
    if (!data.description || data.description.trim().length < 3) {
      newErrors.description = "Descrição deve ter no mínimo 3 caracteres";
    }

    if (data.description.length > PRO014_DESCRIPTION_MAX_LENGTH) {
      newErrors.description = `Descrição deve ter no máximo ${PRO014_DESCRIPTION_MAX_LENGTH} caracteres`;
    }

    // Validação das datas
    if (!data.startDate) {
      newErrors.startDate = "Data de início é obrigatória";
    }

    if (!data.endDate) {
      newErrors.endDate = "Data de fim é obrigatória";
    }

    // Data fim >= Data início
    if (data.startDate && data.endDate) {
      if (!isEndDateValid(data.startDate, data.endDate)) {
        newErrors.endDate = "Data de fim deve ser maior ou igual à data de início";
      }
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error);
  };

  const handleSelectType = (type: CampaignType) => {
    setCampaignType(type);
    setErrors({ ...errors, campaignType: "" });

    // Se mudar para outro tipo, notificar o pai
    if (type !== CampaignType.PRO014 && onChangeType) {
      onChangeType(type);
    }
  };

  const handleNext = () => {
    if (validateForm()) {
      nextStep();
    }
  };

  const isFormValid = useMemo(() => {
    const hasDescription = data.description.trim().length >= 3 && data.description.length <= PRO014_DESCRIPTION_MAX_LENGTH;
    const hasStartDate = !!data.startDate;
    const hasEndDate = !!data.endDate;
    const datesValid = !data.startDate || !data.endDate || isEndDateValid(data.startDate, data.endDate);
    return hasDescription && hasStartDate && hasEndDate && datesValid;
  }, [data.description, data.startDate, data.endDate]);

  return (
    <div className="space-y-4 animate-in fade-in duration-300">
      {/* Campaign Type Selection */}
      <div className="bg-card rounded-lg border p-4">
        <CampaignTypeDropdownSelector
          value={campaignType}
          onChange={handleSelectType}
          error={errors.campaignType}
        />
      </div>

      {/* Basic Information */}
      <div className="bg-card rounded-lg border p-4 space-y-3">
        {/* Description */}
        <div className="space-y-2">
          <Label htmlFor="description" className="text-sm font-medium">
            Descrição da Campanha *
          </Label>
          <Input
            id="description"
            value={data.description}
            onChange={(e) => {
              updateData({ description: e.target.value });
              setErrors({ ...errors, description: "" });
            }}
            placeholder="Ex: 50% na Segunda Peça - Camisetas"
            maxLength={PRO014_DESCRIPTION_MAX_LENGTH}
            className="h-9"
          />
          <p className="text-xs text-muted-foreground">
            {data.description.length}/{PRO014_DESCRIPTION_MAX_LENGTH} caracteres
          </p>
          {errors.description && (
            <p className="text-sm text-destructive flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errors.description}
            </p>
          )}
        </div>

        {/* Date Range */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="startDate" className="text-sm font-medium flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              Data de Início *
            </Label>
            <Input
              id="startDate"
              type="date"
              value={data.startDate}
              onChange={(e) => {
                updateData({ startDate: e.target.value });
                setErrors({ ...errors, startDate: "", endDate: "" });
              }}
              className="h-9"
            />
            {errors.startDate && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.startDate}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="endDate" className="text-sm font-medium flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              Data de Fim *
            </Label>
            <Input
              id="endDate"
              type="date"
              value={data.endDate}
              onChange={(e) => {
                updateData({ endDate: e.target.value });
                setErrors({ ...errors, endDate: "" });
              }}
              className="h-9"
            />
            {errors.endDate && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.endDate}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-2">
        <Button
          variant="outline"
          onClick={() => navigate("/planejamento-comercial/gestao-campanhas")}
          className="w-full md:w-auto"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar para Lista
        </Button>
        <Button
          onClick={handleNext}
          disabled={!isFormValid}
          className="w-full md:w-auto"
        >
          Continuar
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
